import React, { useCallback, useEffect, useState } from 'react';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { useNavigate } from 'react-router-dom';
import ShippingAgentForm from './ShippingAgentForm';
import ShippingAgentRatesFees from './procurement/ShippingAgentRatesFees';
import ConfirmDialog from './ui/ConfirmDialog';
import LoadErrorBox from './ui/LoadErrorBox';
import RecordActivity from './ui/RecordActivity';
import { useLoad } from '../hooks/useLoad';
import { useRole } from '../hooks/useRole';
import { setBarDetail } from '../hooks/useProcurementBar';
import {
  loadShippingAgent,
  createShippingAgent,
  updateShippingAgent,
  archiveShippingAgent,
  restoreShippingAgent,
  deleteShippingAgent,
  CreateShippingAgentInput,
} from '../services/ShippingAgentService';
import { procurementErrorMessage, toSaveError } from '../utils/procurementErrors';
import type { ShippingAgent } from '../types/Procurement';
import { useNotifications } from './NotificationProvider';
import '../styles/components/ProcurementScreens.css';

/**
 * Full-page create/edit for one shipping agent, with its rate cards and fees.
 * Save, Cancel, Archive, Restore and Delete live in the breadcrumb bar (see
 * header/ProcurementBars.tsx); the bar sends 'shipping-agent-*' window events
 * and this page owns the form, the confirm dialogs and the API calls. Agent
 * ids are uuid strings. A viewer sees the page read-only; a contractor sees
 * it without any money (no amounts on the form, no rate or fee panels).
 */

interface Props {
  agentId: string;
}

const isNew = (id: string) => id === 'new';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export default function ShippingAgentEdit({ agentId }: Props) {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { canEdit, canViewFinancials } = useRole();
  const [confirmArchive, setConfirmArchive] = useState(false);

  const load = useLoad<ShippingAgent | null>(() => (isNew(agentId) ? Promise.resolve(null) : loadShippingAgent(agentId)), [agentId]);
  const agent = load.data;

  useEffect(() => {
    if (load.loading || load.error) return;
    window.dispatchEvent(new CustomEvent('shipping-agent-loaded', {
      detail: isNew(agentId)
        ? { label: 'New Agent', active: true, isNew: true }
        : { label: agent?.name ?? 'Agent', active: agent?.active ?? true, isNew: false },
    }));
    setBarDetail(agent ? { kind: 'agent', id: agent.id, title: agent.name, archived: !agent.active } : null);
    return () => setBarDetail(null);
  }, [agentId, agent, load.loading, load.error]);

  const handleCancel = useCallback(() => {
    navigate('/procurement/shipping');
  }, [navigate]);

  // A failed save throws so the form's shared save hook shows the reason on the fields (or a toast).
  const handleSubmit = useCallback(async (payload: CreateShippingAgentInput) => {
    if (!canEdit) return;
    try {
      if (isNew(agentId)) {
        await createShippingAgent(payload);
        showSuccess(`${payload.name} created.`);
      } else if (agent) {
        await updateShippingAgent(agent.id, payload);
        showSuccess(`${payload.name} updated.`);
      }
    } catch (err) {
      throw toSaveError(err, 'Failed to save the agent.');
    }
    navigate('/procurement/shipping');
  }, [agentId, agent, canEdit, navigate, showSuccess]);

  const handleConfirmArchive = useCallback(async () => {
    setConfirmArchive(false);
    if (!agent) return;
    try {
      await archiveShippingAgent(agent.id);
      showInfo(`${agent.name} switched off, not deleted. You can restore it from the Inactive view.`, { persistent: true });
      navigate('/procurement/shipping');
    } catch (err) {
      showError(procurementErrorMessage(err, 'Failed to archive the agent.'));
    }
  }, [agent, navigate, showInfo, showError]);

  const handleRestore = useCallback(async () => {
    if (!agent) return;
    try {
      await restoreShippingAgent(agent.id);
      showSuccess(`${agent.name} restored.`);
      navigate('/procurement/shipping');
    } catch (err) {
      showError(procurementErrorMessage(err, 'Failed to restore the agent.'));
    }
  }, [agent, navigate, showSuccess, showError]);

  // Delete forever is only offered once the agent is inactive. An agent that
  // an order or shipment uses is refused by the server (agent_in_use); the
  // dialog stays open with that reason.
  const forever = useDeleteConfirm<{ id: string; name: string }>({
    record: 'shipping agent',
    perform: a => deleteShippingAgent(a.id),
    onDone: a => {
      showSuccess(`${a.name} deleted.`, { persistent: true });
      navigate('/procurement/shipping');
    },
  });

  useEffect(() => {
    const onArchive = () => { if (canEdit) setConfirmArchive(true); };
    const onRestore = () => { if (canEdit) void handleRestore(); };
    const onDelete = () => { if (canEdit && agent && !agent.active) forever.request({ id: agent.id, name: agent.name }); };
    window.addEventListener('shipping-agent-cancel', handleCancel);
    window.addEventListener('shipping-agent-archive', onArchive);
    window.addEventListener('shipping-agent-restore', onRestore);
    window.addEventListener('shipping-agent-delete', onDelete);
    return () => {
      window.removeEventListener('shipping-agent-cancel', handleCancel);
      window.removeEventListener('shipping-agent-archive', onArchive);
      window.removeEventListener('shipping-agent-restore', onRestore);
      window.removeEventListener('shipping-agent-delete', onDelete);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleCancel, handleRestore, agent, canEdit]);

  if (load.error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="this shipping agent" error={load.error} onRetry={load.reload} retrying={load.loading} /></div>;
  }
  if (load.loading && !agent && !isNew(agentId)) {
    return <div className="sv-empty"><p>Loading agent...</p></div>;
  }
  if (!isNew(agentId) && !agent) {
    return <div className="sv-empty"><p>Shipping agent not found.</p></div>;
  }

  return (
    <div className="detail-layout">
      <div className="inline-form-page">
        {agent && !agent.active && (
          <div className="entity-state-banner entity-state-banner--locked" role="status">
            Switched off. Restore it from the header to use it on orders and shipments, or delete it permanently.
          </div>
        )}
        {!canEdit && (
          <div className="entity-state-banner entity-state-banner--partial" role="status">You can view this agent but not change it.</div>
        )}
        <ShippingAgentForm
          key={agent?.updated_at ?? agentId}
          agent={agent}
          onSubmit={handleSubmit}
          readOnly={!canEdit}
        />

        {agent && canViewFinancials && (
          <ShippingAgentRatesFees agent={agent} canEdit={canEdit} onChanged={load.reload} />
        )}

        <RecordActivity
          entityType="shipping_agent"
          entityId={isNew(agentId) ? undefined : agentId}
          fullWidth
        />
      </div>

      <aside className="detail-sidebar">
        <section className="side-section">
          <h4>Agent Summary</h4>
          <div className="stat-row"><span>Mode</span><strong>{agent?.shipping_mode ? cap(agent.shipping_mode) : '—'}</strong></div>
          <div className="stat-row"><span>Status</span><strong>{agent ? (agent.active ? 'Active' : 'Inactive') : 'New'}</strong></div>
          <div className="stat-row"><span>Offices</span><strong>{agent?.offices?.length ?? 0}</strong></div>
        </section>
      </aside>

      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={() => void handleConfirmArchive()}
        title="Switch off this shipping agent?"
        description="It will be hidden from the active list but not deleted. You can restore it later."
        confirmLabel="Archive"
      />
      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this shipping agent forever?"
        subject={forever.target?.name}
        description="This cannot be undone."
        consequences={['The agent and its rates', 'Its notes and tracking link']}
        confirmLabel="Delete forever"
      />
    </div>
  );
}
